import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FilterAutocomplete from '../../components/common/FilterAutocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LockIcon from '@mui/icons-material/Lock';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import * as accessoryApi from '../../api/accessoryApi';
import * as accessoryGroupApi from '../../api/accessoryGroupApi';
import * as departmentApi from '../../api/departmentApi';
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from '../../context/SnackbarContext';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { getCached, bust } from '../../utils/apiCache';

// Stable sentinel objects — referentially identical on every render so
// Autocomplete isOptionEqualToValue never causes spurious re-renders.
const NO_DEPT_ACC  = Object.freeze({ id: '', label: 'All Departments' });
const NO_GROUP_ACC = Object.freeze({ id: '', label: 'All Groups' });

export default function AccessoriesTab() {
  // Current page's rows only — server-paginated, never the full table.
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Filter state
  const [filterDept, setFilterDept] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterName, setFilterName] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', accessoryGroupId: '', unit: '' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MASTER_DATA') || hasPermission('MANAGE_ACCESSORIES');
  const canDelete = hasPermission('DELETE_MASTER_DATA') || hasPermission('MANAGE_MASTER_DATA') || hasPermission('MANAGE_ACCESSORIES') || hasPermission('DELETE_ACCESSORIES');

  // Reference lists — small and bounded regardless of table size, so these
  // stay cached as before (unlike the accessories list itself).
  useEffect(() => {
    (async () => {
      try {
        const [g, d] = await Promise.all([
          getCached('accessoryGroups:all', () => accessoryGroupApi.getAll()),
          getCached('departments:all', () => departmentApi.getAll()),
        ]);
        setGroups(Array.isArray(g) ? g : (g.data || []));
        setDepartments(Array.isArray(d) ? d : (d.data || []));
      } catch (e) { showSnackbar(e.message, 'error'); }
    })();
  }, [showSnackbar]);

  // ── Server-side paginated + filtered fetch ─────────────────────────────────
  // Accessories are never cached client-side here — at 100k+ rows a per-filter
  // cache entry would just re-create the same localStorage-quota problem the
  // old 'accessories:all' full-fetch had. Live query per page/filter instead.
  const load = useCallback(() => {
    setRefreshing(true);
    const params = { page: page + 1, limit: rowsPerPage };
    if (filterName.trim()) params.search = filterName.trim();
    if (filterGroup) params.accessoryGroupId = filterGroup;
    else if (filterDept) params.departmentId = filterDept;

    accessoryApi.getAll(params)
      .then((res) => {
        setRows(res.data || []);
        setTotal(res.total ?? (res.data ? res.data.length : 0));
      })
      .catch((e) => showSnackbar(e.message, 'error'))
      .finally(() => { setRefreshing(false); setInitialLoading(false); });
  }, [page, rowsPerPage, filterName, filterGroup, filterDept, showSnackbar]);

  // Debounced — covers both filter changes and search keystrokes with one timer.
  // The very first load fires immediately (no artificial delay before the user
  // sees any data); only subsequent filter/search changes get the 300ms debounce.
  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current) { isFirstLoad.current = false; load(); return; }
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Reset selection AND page when filters change
  useEffect(() => { setSelectedIds(new Set()); setPage(0); }, [filterDept, filterGroup, filterName]);

  // ── Scroll to top of the main layout container on every page / page-size change ──
  const scrollSentinelRef = useRef(null);
  useEffect(() => {
    const mainEl = document.getElementById('main-scroll');
    if (mainEl) {
      mainEl.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      scrollSentinelRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, [page, rowsPerPage]);

  // ── Instant client-side filtering (small, bounded reference list) ───────────
  const filteredGroups = useMemo(() =>
    filterDept ? groups.filter((g) => g.departmentId === Number(filterDept)) : groups,
    [groups, filterDept]
  );

  // ── Autocomplete options for toolbar filters ──────────────────────────────
  const deptAccOptions = useMemo(() => [
    NO_DEPT_ACC,
    ...departments.map((d) => ({ id: String(d.id), label: d.name })),
  ], [departments]);

  const groupAccOptions = useMemo(() => [
    NO_GROUP_ACC,
    ...filteredGroups.map((g) => ({
      id: String(g.id),
      label: filterDept ? g.groupCode : `${g.groupCode} — ${g.department?.name}`,
    })),
  ], [filteredGroups, filterDept]);

  const currentDeptAcc  = deptAccOptions.find((o) => o.id === String(filterDept))  ?? NO_DEPT_ACC;
  const currentGroupAcc = groupAccOptions.find((o) => o.id === String(filterGroup)) ?? NO_GROUP_ACC;

  // ── Filter helpers ────────────────────────────────────────────────────────────
  const handleDeptChange = (deptId) => { setFilterDept(deptId); setFilterGroup(''); };
  const clearFilters = () => { setFilterDept(''); setFilterGroup(''); setFilterName(''); };
  const hasFilters = filterDept || filterGroup || filterName;

  // ── Selection helpers — "select all" is page-scoped (matches PriceManagement's
  // existing bulk-select behavior); cross-page selection still works if the user
  // manually checks rows on more than one page before deleting.
  const toggleRow = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => (rows.length > 0 && rows.every((a) => selectedIds.has(a.id)))
    ? setSelectedIds(new Set())
    : setSelectedIds(new Set(rows.map((a) => a.id)));
  const allSelected = rows.length > 0 && rows.every((a) => selectedIds.has(a.id));
  const someSelected = rows.some((a) => selectedIds.has(a.id)) && !allSelected;

  // ── Bulk delete ───────────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    try {
      const res = await accessoryApi.batchRemove([...selectedIds]);
      showSnackbar(res.message || `${selectedIds.size} accessory(ies) deleted.`, 'success');
      setSelectedIds(new Set());
      // The Dashboard's Accessories count card reads from this same cache.
      bust('stats');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', accessoryGroupId: filterGroup || '', unit: '' });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditItem(row);
    setForm({ name: row.name, accessoryGroupId: row.accessoryGroupId, unit: row.unit });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.accessoryGroupId) return;
    if (!editItem && !form.unit.trim()) return;
    setSaving(true);
    try {
      if (editItem) {
        await accessoryApi.update(editItem.id, { name: form.name, accessoryGroupId: form.accessoryGroupId });
        showSnackbar('Accessory updated', 'success');
      } else {
        await accessoryApi.create(form);
        showSnackbar('Accessory created', 'success');
      }
      setDialogOpen(false);
      bust('stats');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await accessoryApi.remove(deleteTarget.id);
      showSnackbar(`"${deleteTarget.name}" deleted.`, 'success');
      setDeleteTarget(null);
      bust('stats');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  return (
    <Box ref={scrollSentinelRef}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterListIcon color="action" fontSize="small" />
          <FilterAutocomplete
            label="Department"
            size="small"
            sx={{ minWidth: 180 }}
            options={deptAccOptions}
            value={currentDeptAcc}
            onChange={(_, opt) => handleDeptChange(opt?.id || '')}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
          <FilterAutocomplete
            label="Group Code"
            size="small"
            sx={{ minWidth: 200 }}
            options={groupAccOptions}
            value={currentGroupAcc}
            onChange={(_, opt) => setFilterGroup(opt?.id || '')}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
          <TextField
            size="small"
            placeholder="Search by name…"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /></InputAdornment> }}
            inputProps={{ autoComplete: 'off' }}
            sx={{ minWidth: 190 }}
          />
          {hasFilters && (
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>Clear</Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {total} accessory(ies)
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Accessory
          </Button>
        )}
      </Box>

      {!canManage && !canDelete && (
        <Alert severity="info" sx={{ mb: 2 }}>You have read-only access to accessories.</Alert>
      )}

      {/* Bulk action toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 2, border: '1px solid #FFB300' }}>
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} accessory(ies) selected</Typography>
          <Button variant="contained" color="error" size="small" startIcon={<DeleteSweepIcon />} onClick={() => setBulkDeleteOpen(true)}>
            Delete Selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ ml: 'auto' }}>Clear</Button>
        </Box>
      )}

      {/* Thin progress bar — visible on initial load AND every subsequent
          filter/page fetch, since the list is now a live server query. */}
      {refreshing && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {/* Table */}
      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F5F5F5' }}>
              {canDelete && <TableCell padding="checkbox"><Checkbox size="small" indeterminate={someSelected} checked={allSelected} onChange={toggleAll} /></TableCell>}
              <TableCell sx={{ width: 40 }}>#</TableCell>
              <TableCell>Accessory Name</TableCell>
              <TableCell>Group Code</TableCell>
              <TableCell>Department</TableCell>
              <TableCell sx={{ width: 90 }}>
                Unit&nbsp;
                <Tooltip title="Unit is fixed after creation"><LockIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: '#bbb' }} /></Tooltip>
              </TableCell>
              <TableCell sx={{ width: 80 }}>Prices</TableCell>
              <TableCell sx={{ width: 80 }}>History</TableCell>
              {(canManage || canDelete) && <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {initialLoading ? (
              // Skeleton rows — shown only on first visit, before the first
              // response has ever come back.
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {canDelete && <TableCell padding="checkbox"><Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: 0.5 }} /></TableCell>}
                  <TableCell><Skeleton width={24} /></TableCell>
                  <TableCell><Skeleton width={`${50 + (i % 4) * 12}%`} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={72} height={22} /></TableCell>
                  <TableCell><Skeleton width="55%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={44} height={22} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={32} height={22} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={32} height={22} /></TableCell>
                  {(canManage || canDelete) && <TableCell />}
                </TableRow>
              ))
            ) : (
              <>
                {rows.map((a, i) => { const globalIdx = page * rowsPerPage + i; return (
                  <TableRow key={a.id} hover selected={selectedIds.has(a.id)} sx={{ bgcolor: selectedIds.has(a.id) ? '#FFF8E1 !important' : undefined }}>
                    {canDelete && <TableCell padding="checkbox"><Checkbox size="small" checked={selectedIds.has(a.id)} onChange={() => toggleRow(a.id)} /></TableCell>}
                    <TableCell>{globalIdx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{a.name}</TableCell>
                    <TableCell><Chip label={a.accessoryGroup?.groupCode} size="small" color="secondary" variant="outlined" /></TableCell>
                    <TableCell sx={{ color: '#757575' }}>{a.accessoryGroup?.department?.name}</TableCell>
                    <TableCell><Chip label={a.unit} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Chip label={a._count?.prices || 0} size="small" color={a._count?.prices > 0 ? 'success' : 'default'} variant={a._count?.prices > 0 ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell><Chip label={a._count?.priceHistory || 0} size="small" variant="outlined" /></TableCell>
                    {(canManage || canDelete) && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {canManage && (
                          <Tooltip title="Edit accessory">
                            <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete accessory">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(a)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ); })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canDelete ? 9 : (canManage ? 8 : 7)} align="center" sx={{ py: 4, color: '#9e9e9e' }}>
                      {hasFilters ? 'No accessories match the selected filters.' : 'No accessories found. Add one above.'}
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
          {!initialLoading && total > 0 && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={canDelete ? 9 : (canManage ? 8 : 7)}
                  count={total}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[50, 100, 200]}
                  labelRowsPerPage="Rows:"
                  sx={{ borderBottom: 0 }}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Accessory' : 'New Accessory'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            autoFocus
            label="Accessory Name"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>Accessory Group</InputLabel>
            <Select
              value={form.accessoryGroupId}
              label="Accessory Group"
              onChange={(e) => setForm({ ...form, accessoryGroupId: e.target.value })}
            >
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.groupCode} — {g.department?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!editItem ? (
            <TextField
              label="Unit (e.g. NOS, SET, MTR)"
              fullWidth
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              helperText="Unit cannot be changed after creation"
            />
          ) : (
            <TextField
              label="Unit"
              fullWidth
              value={form.unit}
              disabled
              helperText={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon sx={{ fontSize: 12 }} />
                  Unit is fixed after creation and cannot be changed
                </Box>
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!form.name.trim() || !form.accessoryGroupId || (!editItem && !form.unit.trim()) || saving}
          >
            {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.size} Accessory(ies)`}
        message={`Delete ${selectedIds.size} selected accessory(ies) and ALL associated price data?\n\nThis cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Accessory"
        message={`Delete "${deleteTarget?.name}"?\n\nThis will also permanently remove:\n• ${deleteTarget?._count?.prices || 0} price record(s)\n• ${deleteTarget?._count?.priceHistory || 0} price history entry/entries\n\nThis action cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
